/**
 * PR Polygon Service
 *
 * Frontend service for the PR-by-polygon snapshot dataset. Talks to the
 * parasite-rate API (served by the same backend Lambda) via API Gateway:
 *
 *   GET /api/parasite-rate/polygons            → the single polygon snapshot dataset
 *   GET /api/parasite-rate/polygons/versions   → available polygon dataset versions
 *
 * Unlike the cluster dataset, the polygon export is a single, non-partitioned
 * parquet file per version, so a single request loads the whole dataset. Rows
 * are typed to the known parquet schema (see {@link PolygonRow}). The backend
 * excludes the WKB geometry column from the response — as text it would blow
 * past Lambda's 6MB response limit and it is not shown in the grid.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A single polygon dataset cell value as returned by the API.
 */
export type PolygonCellValue = string | number | boolean | null

/**
 * The value shape a polygon column holds once parsed: a number column or a
 * string column. (All columns are additionally nullable in {@link PolygonRow}.)
 */
type PolygonColumnKind = "number" | "string"

/**
 * Single source of truth for the PR-by-polygon snapshot schema: every column in
 * the dataset's documented order, each tagged with the kind its values parse
 * to.
 *
 * `as const satisfies ...` keeps the exact literal names/kinds (needed to derive
 * a precise PolygonRow type) while still validating each entry's shape.
 */
const POLYGON_COLUMN_SPECS = [
  { name: "biblio_id", kind: "string" },
  { name: "table_on_page", kind: "string" },
  { name: "admin_unit_name", kind: "string" },
  { name: "geom_id", kind: "string" },
  { name: "geometry_level", kind: "string" },
  { name: "iso3", kind: "string" },
  { name: "shapefile_s3_uri", kind: "string" },
  { name: "map_survey_id", kind: "string" },
  { name: "dhs_survey_id", kind: "string" },
  { name: "dhs_survey_num", kind: "string" },
  { name: "year_start", kind: "number" },
  { name: "month_start", kind: "number" },
  { name: "day_start", kind: "number" },
  { name: "year_end", kind: "number" },
  { name: "month_end", kind: "number" },
  { name: "day_end", kind: "number" },
  { name: "lower_age_months", kind: "number" },
  { name: "upper_age_months", kind: "number" },
  { name: "examined", kind: "number" },
  { name: "method", kind: "string" },
  { name: "rdt_type", kind: "string" },
  { name: "any_positive", kind: "number" },
  { name: "any_pr", kind: "number" },
  { name: "pf_positive", kind: "number" },
  { name: "pf_pr", kind: "number" },
  { name: "pv_positive", kind: "number" },
  { name: "pv_pr", kind: "number" },
  { name: "pm_positive", kind: "number" },
  { name: "pm_pr", kind: "number" },
  { name: "po_positive", kind: "number" },
  { name: "po_pr", kind: "number" },
  { name: "pk_positive", kind: "number" },
  { name: "pk_pr", kind: "number" },
  { name: "pf_pan_positive", kind: "number" },
  { name: "pf_pan_pr", kind: "number" },
  { name: "pv_pan_positive", kind: "number" },
  { name: "pv_pan_pr", kind: "number" },
  { name: "pf_pv_pan_positive", kind: "number" },
  { name: "pf_pv_pan_pr", kind: "number" },
  { name: "pan_positive_only", kind: "number" },
  { name: "pan_pr_only", kind: "number" },
] as const satisfies readonly { name: string; kind: PolygonColumnKind }[]

/** The union of all column spec entries, used to derive the row type below. */
type PolygonColumnSpec = (typeof POLYGON_COLUMN_SPECS)[number]

/**
 * A single row of the PR-by-polygon snapshot dataset, derived from
 * {@link POLYGON_COLUMN_SPECS}. Each column becomes a key whose value is
 * `number | null` for numeric columns and `string | null` for the rest.
 */
export type PolygonRow = {
  [Spec in PolygonColumnSpec as Spec["name"]]:
    (Spec["kind"] extends "number" ? number : string) | null
}

/**
 * Column names in the dataset's documented order. Drives both row construction
 * and grid column order, independent of the key order the API returns.
 */
export const POLYGON_COLUMNS: readonly (keyof PolygonRow)[] =
  POLYGON_COLUMN_SPECS.map((spec) => spec.name)

/**
 * Human-readable column headers for the grid, keyed by column name. Columns
 * without an entry fall back to a title-cased version of the raw name.
 */
export const POLYGON_COLUMN_LABELS: Partial<Record<keyof PolygonRow, string>> = {
  biblio_id: "Biblio ID",
  table_on_page: "Table on Page",
  admin_unit_name: "Admin Unit",
  geom_id: "Geometry ID",
  geometry_level: "Geometry Level",
  iso3: "ISO3",
  shapefile_s3_uri: "Shapefile S3 URI",
  map_survey_id: "MAP Survey ID",
  dhs_survey_id: "DHS Survey ID",
  dhs_survey_num: "DHS Survey No.",
  year_start: "Year Start",
  month_start: "Month Start",
  day_start: "Day Start",
  year_end: "Year End",
  month_end: "Month End",
  day_end: "Day End",
  lower_age_months: "Lower Age (months)",
  upper_age_months: "Upper Age (months)",
  examined: "Examined",
  method: "Method",
  rdt_type: "RDT Type",
  any_positive: "Any Positive",
  any_pr: "Any PR",
  pf_positive: "Pf Positive",
  pf_pr: "Pf PR",
  pv_positive: "Pv Positive",
  pv_pr: "Pv PR",
  pm_positive: "Pm Positive",
  pm_pr: "Pm PR",
  po_positive: "Po Positive",
  po_pr: "Po PR",
  pk_positive: "Pk Positive",
  pk_pr: "Pk PR",
  pf_pan_positive: "Pf-pan Positive",
  pf_pan_pr: "Pf-pan PR",
  pv_pan_positive: "Pv-pan Positive",
  pv_pan_pr: "Pv-pan PR",
  pf_pv_pan_positive: "Pf-Pv-pan Positive",
  pf_pv_pan_pr: "Pf-Pv-pan PR",
  pan_positive_only: "Pan Positive (only)",
  pan_pr_only: "Pan PR (only)",
}

/** Numeric column names, used to coerce incoming values to numbers (or null). */
const POLYGON_NUMERIC_COLUMNS: ReadonlySet<keyof PolygonRow> = new Set(
  POLYGON_COLUMN_SPECS
    .filter((spec) => spec.kind === "number")
    .map((spec) => spec.name),
)

/**
 * Response shape from GET /api/parasite-rate/polygons.
 *
 * Data is column-oriented (each key maps to an array of values), mirroring the
 * cluster endpoints.
 */
interface PolygonDataResponse {
  /** The resolved dataset version (e.g. "v1.0"). */
  version: string
  /** Number of rows in the dataset. */
  row_count: number
  /** Column-oriented data (column_name → values[]). */
  data: Record<string, PolygonCellValue[]>
}

/**
 * Response shape from GET /api/parasite-rate/polygons/versions.
 */
interface PolygonVersionsResponse {
  /** The latest (highest-minor) version within the supported major. */
  latest_version: string
  /** All supported-major version strings, newest first (e.g. ["v1.2", "v1.1"]). */
  versions: string[]
}

/**
 * The extra column name present only in the validation-failures dataset. It
 * describes why a given row failed validation. Not part of {@link POLYGON_COLUMNS}
 * since the main snapshot does not carry it.
 */
export const VALIDATION_ERROR_COLUMN: "validation_error" = "validation_error"

/**
 * Human-readable header for the {@link VALIDATION_ERROR_COLUMN}, used by the
 * grid when rendering the failures view.
 */
export const VALIDATION_ERROR_LABEL: string = "Validation Error"

/**
 * A single validation-failure row: every {@link PolygonRow} column plus the
 * `validation_error` describing why the row failed validation.
 */
export type PolygonFailureRow = PolygonRow & {
  /** The validation error message for this row (null when absent). */
  validation_error: string | null
}

/**
 * The fully loaded polygon dataset: the resolved version and the row-oriented
 * typed records.
 */
export interface PolygonDataset {
  /** The resolved dataset version the rows belong to. */
  version: string
  /** Row-oriented records, one per row in the dataset. */
  rows: PolygonRow[]
}

// ---------------------------------------------------------------------------
// API Base URL
// ---------------------------------------------------------------------------

/** Cached API base URL (shared parasite-rate API root) loaded once. */
let POLYGON_API_URL: string = ""

/**
 * Load the parasite-rate API base URL from the build-time env var or
 * aws-exports.json. The polygon routes live under this same API root.
 *
 * @returns The base URL for the parasite-rate API (e.g. "https://host/api/parasite-rate" or "/api/parasite-rate").
 */
async function loadApiUrl(): Promise<string> {
  if (POLYGON_API_URL) {
    return POLYGON_API_URL
  }

  // VITE_API_URL is baked in at build time for deployed mode.
  const envUrl: string = import.meta.env.VITE_API_URL || ""
  if (envUrl) {
    POLYGON_API_URL = `${envUrl}/api/parasite-rate`
    return POLYGON_API_URL
  }

  // Fall back to the static aws-exports.json config for CI-built deployments,
  // and finally to a relative path (dev / same-origin) when no apiUrl is set.
  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = config.apiUrl || ""
    POLYGON_API_URL = baseUrl ? `${baseUrl}/api/parasite-rate` : "/api/parasite-rate"
    return POLYGON_API_URL
  } catch (error) {
    // A missing/unreadable config is not fatal: on dev the relative path works
    // via the proxy. Log for visibility but still resolve to the relative root.
    console.error("prPolygonService: failed to load aws-exports.json, using relative API path:", error)
    POLYGON_API_URL = "/api/parasite-rate"
    return POLYGON_API_URL
  }
}

// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------

/**
 * Fetch the list of available polygon dataset versions (within the backend's
 * supported major), newest first.
 *
 * @param token - Auth0 access token for API authentication.
 * @returns The versions response with the latest version and full list.
 * @throws Error if the API call fails.
 */
export async function getPolygonVersions(token: string): Promise<PolygonVersionsResponse> {
  const apiUrl: string = await loadApiUrl()

  const response = await fetch(`${apiUrl}/polygons/versions`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to fetch polygon versions: HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch the raw polygon snapshot dataset for a specific version (or the latest
 * supported version when omitted).
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to query (e.g. "v1.0"). If omitted, the backend returns the latest supported version.
 * @returns The column-oriented polygon data response.
 * @throws Error if the API call fails.
 */
export async function getPolygonData(token: string, version?: string): Promise<PolygonDataResponse> {
  const apiUrl: string = await loadApiUrl()

  const url: string = version
    ? `${apiUrl}/polygons?version=${encodeURIComponent(version)}`
    : `${apiUrl}/polygons`
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to fetch polygon data: HTTP ${response.status}`)
  }

  return response.json()
}

// ---------------------------------------------------------------------------
// Data Transformation
// ---------------------------------------------------------------------------

/**
 * Coerce a raw cell value into the number a numeric column expects.
 *
 * Returns null for null/empty values. A value that cannot be parsed as a finite
 * number is also returned as null (the source value is logged) rather than
 * producing a NaN that would silently corrupt downstream numeric handling.
 *
 * @param value - The raw cell value from the API.
 * @param column - The column name, used only for diagnostic logging.
 * @returns The parsed finite number, or null.
 */
function toNumberOrNull(
  { value, column }: { value: PolygonCellValue; column: keyof PolygonRow },
): number | null {
  if (value == null || value === "") {
    return null
  }
  const parsed: number = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(parsed)) {
    console.warn(`[prPolygonService] Non-numeric value in numeric column '${String(column)}':`, value)
    return null
  }
  return parsed
}

/**
 * Coerce a raw cell value into the string a text column expects.
 *
 * Returns null for null values; any other value is stringified so numeric-like
 * identifier columns (e.g. survey ids) are preserved as strings.
 *
 * @param value - The raw cell value from the API.
 * @returns The value as a string, or null.
 */
function toStringOrNull({ value }: { value: PolygonCellValue }): string | null {
  if (value == null) {
    return null
  }
  return String(value)
}

/**
 * Convert a column-oriented polygon response into typed, row-oriented records.
 *
 * Rows are built strictly from the known schema ({@link POLYGON_COLUMNS}) so
 * the output shape is stable regardless of the key order the API returns.
 * Numeric columns are coerced to numbers; all other columns are strings.
 *
 * @param data - Column-oriented data (column_name → values[]).
 * @param rowCount - Number of rows in the dataset.
 * @returns The typed row-oriented records.
 */
function columnsToRows(
  { data, rowCount }: { data: Record<string, PolygonCellValue[]>; rowCount: number },
): PolygonRow[] {
  const rows: PolygonRow[] = []

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    // Start from a partial and populate every known column so the result is a
    // complete PolygonRow. Columns absent from the response resolve to null.
    const row: Partial<PolygonRow> = {}

    for (const column of POLYGON_COLUMNS) {
      // Each column is an array aligned by row index.
      const rawValue: PolygonCellValue = data[column]?.[rowIndex] ?? null
      if (POLYGON_NUMERIC_COLUMNS.has(column)) {
        // Numeric column — assign a number|null. The cast is safe because the
        // POLYGON_NUMERIC_COLUMNS set only contains numeric-typed keys.
        (row[column] as number | null) = toNumberOrNull({ value: rawValue, column })
      } else {
        // Text column — assign a string|null.
        (row[column] as string | null) = toStringOrNull({ value: rawValue })
      }
    }

    rows.push(row as PolygonRow)
  }

  return rows
}

// ---------------------------------------------------------------------------
// Dataset Loader (with version-based caching)
// ---------------------------------------------------------------------------

/**
 * In-memory cache of the last-loaded polygon dataset, keyed by version. The
 * polygon dataset is a single file, so caching the whole dataset is cheap and
 * avoids re-downloading on version re-selection.
 */
let _cachedVersion: string | null = null
let _cachedDataset: PolygonDataset | null = null

/**
 * Load the polygon snapshot dataset for a version (or the latest supported
 * version when omitted), returning row-oriented records ready for the grid.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to load (e.g. "v1.0"). If omitted, the backend returns the latest supported version.
 * @returns The loaded dataset (resolved version, column names, and rows).
 * @throws Error if the API call fails.
 */
export async function loadPolygonData(token: string, version?: string): Promise<PolygonDataset> {

  const targetVersion: string = version != null
    ? version
    : (await getPolygonVersions(token)).latest_version

  // Serve from cache when the resolved version matches what we already hold.
  if (_cachedVersion === targetVersion && _cachedDataset != null) {
    return _cachedDataset
  }

  // Always request the concrete version now, never the implicit "latest".
  const response: PolygonDataResponse = await getPolygonData(token, targetVersion)
  const { version: resolvedVersion, data, row_count } = response

  const rows: PolygonRow[] = columnsToRows({ data, rowCount: row_count })

  const dataset: PolygonDataset = {
    version: resolvedVersion,
    rows,
  }

  _cachedVersion = resolvedVersion
  _cachedDataset = dataset

  return dataset
}

// ---------------------------------------------------------------------------
// Validation Failures
// ---------------------------------------------------------------------------

/**
 * The fully loaded polygon validation-failures dataset: the resolved version
 * and the row-oriented failure records.
 */
export interface PolygonFailuresDataset {
  /** The resolved dataset version the failure rows belong to. */
  version: string
  /** Row-oriented failure records, one per failed row. */
  rows: PolygonFailureRow[]
}

/**
 * Fetch the raw polygon validation-failures dataset for a version (or the
 * latest supported version when omitted).
 *
 * The response has the same column-oriented shape as the data endpoint, with an
 * additional `validation_error` column. A version with no failures returns an
 * empty `data` object.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to query (e.g. "v1.1"). If omitted, the backend returns the latest supported version.
 * @returns The column-oriented validation-failures response.
 * @throws Error if the API call fails.
 */
async function getPolygonValidationFailures(
  token: string,
  version?: string,
): Promise<PolygonDataResponse> {
  const apiUrl: string = await loadApiUrl()

  const url: string = version
    ? `${apiUrl}/polygons/validation-failures?version=${encodeURIComponent(version)}`
    : `${apiUrl}/polygons/validation-failures`
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to fetch polygon validation failures: HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * In-memory cache of the last-loaded failures dataset, keyed by version. Kept
 * separate from the data cache so toggling failure mode on and off does not
 * evict the main dataset (and vice versa).
 */
let _cachedFailuresVersion: string | null = null
let _cachedFailuresDataset: PolygonFailuresDataset | null = null

/**
 * Load the polygon validation-failures dataset for a version (or the latest
 * supported version when omitted), returning row-oriented records ready for the
 * grid.
 *
 * Each row carries every {@link PolygonRow} column (built via the same transpose
 * as the main dataset) plus the extra `validation_error` string, which is read
 * directly from the column-oriented response.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to load (e.g. "v1.1"). If omitted, the backend returns the latest supported version.
 * @returns The loaded failures dataset (resolved version and failure rows).
 * @throws Error if the API call fails.
 */
export async function loadPolygonValidationFailures(
  token: string,
  version?: string,
): Promise<PolygonFailuresDataset> {
  const targetVersion: string = version != null
    ? version
    : (await getPolygonVersions(token)).latest_version

  // Serve from cache when the resolved version matches what we already hold.
  if (_cachedFailuresVersion === targetVersion && _cachedFailuresDataset != null) {
    return _cachedFailuresDataset
  }

  const response: PolygonDataResponse = await getPolygonValidationFailures(token, targetVersion)
  const { version: resolvedVersion, data, row_count } = response

  // Build the base PolygonRow columns with the shared transpose, then attach
  // the failures-only validation_error column read straight from the response.
  const baseRows: PolygonRow[] = columnsToRows({ data, rowCount: row_count })
  const errorValues: PolygonCellValue[] = data[VALIDATION_ERROR_COLUMN] ?? []

  const rows: PolygonFailureRow[] = baseRows.map((row, rowIndex): PolygonFailureRow => ({
    ...row,
    validation_error: toStringOrNull({ value: errorValues[rowIndex] ?? null }),
  }))

  const dataset: PolygonFailuresDataset = {
    version: resolvedVersion,
    rows,
  }

  _cachedFailuresVersion = resolvedVersion
  _cachedFailuresDataset = dataset

  return dataset
}
